/**
 * Auth + client profile helpers for videogen embedded in Brandigiops iframe.
 */

const AUTH_MESSAGE_TYPE = "BRANDIGIOPS_AUTH";
const AUTH_REQUEST_TYPE = "BRANDIGIOPS_AUTH_REQUEST";
const CLIENT_PROFILE_MESSAGE_TYPE = "BRANDIGIOPS_CLIENT_PROFILE";
const CLIENT_PROFILE_REQUEST_TYPE = "BRANDIGIOPS_CLIENT_PROFILE_REQUEST";
const TOKEN_STORAGE_KEY = "brandigiops_auth_token";

const ALLOWED_PARENT_ORIGINS = [
  "https://marketops.speedstack.tech",
  "http://localhost:8080",
  "http://localhost:3000",
  "http://localhost:5173",
  "http://127.0.0.1:3000",
  "http://127.0.0.1:5173",
  "http://127.0.0.1:8080",
];

export const isAllowedParentOrigin = (origin) =>
  ALLOWED_PARENT_ORIGINS.includes(origin);

/** S3 presigned URLs may embed the real logo URL — extract it when present */
export const resolveLogoUrl = (logoImage) => {
  if (!logoImage) return null;

  const idx = logoImage.indexOf("https%3A");
  if (idx !== -1) {
    try {
      const encoded = logoImage.slice(idx).split("?")[0];
      return decodeURIComponent(encoded);
    } catch {
      // fall through
    }
  }

  return logoImage;
};

const readDigiopsAuthToken = () => {
  try {
    const raw = localStorage.getItem("digiops_auth");
    if (!raw) return null;
    return JSON.parse(raw)?.user?.accessToken ?? null;
  } catch {
    return null;
  }
};

export const getAuthToken = () =>
  sessionStorage.getItem(TOKEN_STORAGE_KEY) ||
  readDigiopsAuthToken() ||
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("accessToken") ||
  sessionStorage.getItem("token") ||
  sessionStorage.getItem("authToken") ||
  null;

export const storeAuthToken = (token) => {
  if (token) sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
};

const postToParent = (message, clientId) => {
  if (window.parent === window) return;
  ALLOWED_PARENT_ORIGINS.forEach((origin) => {
    try {
      window.parent.postMessage(
        clientId != null ? { ...message, clientId } : message,
        origin,
      );
    } catch {
      // ignore invalid target
    }
  });
};

export const requestAuthFromParent = () => {
  postToParent({ type: AUTH_REQUEST_TYPE });
};

export const requestClientProfileFromParent = (clientId) => {
  if (!clientId) return;
  postToParent({ type: CLIENT_PROFILE_REQUEST_TYPE }, clientId);
};

export const subscribeToBrandigiopsAuth = (onToken) => {
  const handler = (event) => {
    if (!isAllowedParentOrigin(event.origin)) return;
    if (event.data?.type !== AUTH_MESSAGE_TYPE) return;

    const token = event.data.token;
    if (token) {
      storeAuthToken(token);
      onToken(token);
    }
  };

  window.addEventListener("message", handler);
  requestAuthFromParent();

  return () => window.removeEventListener("message", handler);
};

export const subscribeToBrandigiopsClientProfile = (onProfile) => {
  const handler = (event) => {
    if (!isAllowedParentOrigin(event.origin)) return;
    if (event.data?.type !== CLIENT_PROFILE_MESSAGE_TYPE) return;

    const logoRaw = event.data.logoUrl || event.data.logoImage;
    const businessInfo = event.data.businessInfo ?? {};
    onProfile({
      companyName:
        event.data.companyName ||
        event.data.name ||
        event.data.brandName ||
        businessInfo.name ||
        null,
      logoUrl: resolveLogoUrl(logoRaw || businessInfo.logoImage),
    });
  };

  window.addEventListener("message", handler);

  return () => window.removeEventListener("message", handler);
};

export const fetchClientOnboarding = async (clientId, authToken) => {
  const baseUrl = "https://api-uat.brandigiops.com";
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(`${baseUrl}/clientOnboarding/${clientId}`, {
    method: "GET",
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`Profile fetch failed: ${res.status}`);
  }

  const data = await res.json();
  const businessInfo = data?.businessInfo ?? {};

  return {
    companyName:
      businessInfo.name ||
      data?.website?.[0]?.brandName ||
      null,
    logoUrl: resolveLogoUrl(businessInfo.logoImage),
  };
};
